import { Link } from "react-router-dom";
import { ExternalLink, Settings } from "lucide-react";
import { useConsent } from "@/components/consent/ConsentProvider";

const BROWSER_GUIDES = [
  { name: "Google Chrome", url: "https://support.google.com/chrome/answer/95647" },
  { name: "Mozilla Firefox", url: "https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop" },
  { name: "Apple Safari", url: "https://support.apple.com/it-it/guide/safari/sfri11471/mac" },
  { name: "Microsoft Edge", url: "https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" },
];

export default function Cookie() {
  const { openPreferences, revoke, hasChosen } = useConsent();
  return (
    <div data-testid="page-cookie" className="pt-28 pb-32">
      <section className="container-x py-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <span className="overline">Cookie Policy</span>
        </div>
        <div className="md:col-span-8">
          <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] tracking-tight">
            Informativa <span className="italic text-primary">Cookie</span>
          </h1>
          <p className="mt-4 text-primary font-medium">Termini e condizioni</p>
        </div>
      </section>

      <section className="container-x grid md:grid-cols-12 gap-16">
        <div className="md:col-span-8 md:col-start-3 space-y-12 text-muted-foreground leading-relaxed">
          {/* Preference manager */}
          <div className="bg-foreground text-background p-8 sm:p-10" data-testid="cookie-manage-box">
            <p className="text-[10px] uppercase tracking-[0.32em] text-secondary font-semibold">
              Gestione preferenze
            </p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl leading-tight text-background">
              Modifica in ogni momento le tue<br />
              <span className="italic text-secondary">scelte sui cookie</span>.
            </h2>
            <p className="mt-4 text-sm text-background/70 max-w-xl">
              {hasChosen
                ? "Le tue preferenze sono attive. Puoi rivedere le categorie oppure ritirare completamente il consenso."
                : "Non hai ancora espresso una scelta. Personalizza le categorie che vuoi attivare."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openPreferences}
                data-testid="cookie-open-prefs"
                className="btn-base bg-secondary text-foreground px-6 py-3 hover:bg-secondary/85 text-sm"
              >
                <Settings className="h-4 w-4" /> Modifica preferenze
              </button>
              {hasChosen && (
                <button
                  type="button"
                  onClick={revoke}
                  data-testid="cookie-revoke"
                  className="btn-base border border-background/30 text-background px-6 py-3 hover:bg-background hover:text-foreground text-sm"
                >
                  Ritira il consenso
                </button>
              )}
            </div>
          </div>
          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-foreground">Cosa sono i cookie?</h2>
            <p className="mt-4 text-lg">
              I cookie sono piccoli file di testo che i siti web visitati inviano al dispositivo dell'utente (computer,
              smartphone, tablet), dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita
              successiva.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-foreground">Tipologie di cookie utilizzati</h2>
            <p className="mt-4">Il nostro sito web utilizza le seguenti tipologie di cookie:</p>

            <div className="mt-8 space-y-6">
              <div className="border border-foreground/10 p-6 sm:p-8">
                <p className="overline">01</p>
                <h3 className="mt-3 font-display text-2xl text-foreground">Cookie Tecnici (necessari)</h3>
                <p className="mt-3">
                  Questi cookie sono essenziali per il corretto funzionamento del sito e non richiedono il consenso
                  dell'utente. Possono includere:
                </p>
                <ul className="mt-3 space-y-2 pl-6 list-disc">
                  <li>Cookie di sessione per la navigazione</li>
                  <li>Cookie di autenticazione</li>
                  <li>Cookie per il salvataggio delle preferenze dell'utente</li>
                </ul>
              </div>

              <div className="border border-foreground/10 p-6 sm:p-8">
                <p className="overline">02</p>
                <h3 className="mt-3 font-display text-2xl text-foreground">Cookie di Analisi e Performance (statistici)</h3>
                <p className="mt-3">
                  Questi cookie ci aiutano a comprendere come gli utenti interagiscono con il sito, raccogliendo
                  informazioni anonime sulle visite e sulle pagine più visualizzate. Usiamo strumenti come Google
                  Analytics.
                </p>
              </div>

              <div className="border border-foreground/10 p-6 sm:p-8">
                <p className="overline">03</p>
                <h3 className="mt-3 font-display text-2xl text-foreground">Cookie di Profilazione e Marketing</h3>
                <p className="mt-3">
                  Questi cookie vengono utilizzati per personalizzare i contenuti pubblicitari e monitorare l'efficacia
                  delle campagne pubblicitarie. Possono essere di terze parti (ad es. Google Ads, Facebook Pixel).
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-foreground">Gestione del consenso ai cookie</h2>
            <p className="mt-4">
              Quando visiti il nostro sito per la prima volta, ti viene mostrato un banner che ti permette di:
            </p>
            <ul className="mt-3 space-y-2 pl-6 list-disc">
              <li>Accettare tutti i cookie</li>
              <li>Rifiutare i cookie non essenziali</li>
              <li>Personalizzare le preferenze</li>
            </ul>
            <p className="mt-4">
              Puoi modificare le tue preferenze in qualsiasi momento accedendo alla sezione{" "}
              <em className="text-foreground not-italic font-medium">"Gestione Cookie"</em> presente nel sito.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-foreground">Come disabilitare i cookie dal browser</h2>
            <p className="mt-4">
              Se desideri bloccare o eliminare i cookie, puoi farlo direttamente dalle impostazioni del tuo browser:
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {BROWSER_GUIDES.map((b) => (
                <li key={b.name}>
                  <a
                    href={b.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-primary underline-link"
                    data-testid={`browser-guide-${b.name.toLowerCase().split(" ")[0]}`}
                  >
                    {b.name} → Guida <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl sm:text-3xl text-foreground">Maggiori informazioni</h2>
            <p className="mt-4">
              Per ulteriori dettagli sul trattamento dei dati personali, ti invitiamo a leggere la nostra{" "}
              <Link to="/privacy" className="text-primary underline-link" data-testid="cookie-to-privacy">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
